import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import type { FC } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/Badge";
import { Typography } from "@/components/Typography";
import { PageLayout } from "@/pages/components/PageLayout";
import { PageTitle } from "@/pages/components/PageTitle";
import { MaacUsers } from "@/pages/userManagement/components/MaacUsers";
import {
  MAAC_MEMBERS_ANCHOR_ID,
  ProductAccessCards,
} from "@/pages/userManagement/components/ProductAccessCards";

/**
 * One card per product, then the MAAC member table: MAAC roles are the only
 * access managed in Admin Center. The route still accepts `?tab=` so links
 * from MAAC (`?tab=maac&target-edit-user-email=...`) keep working; the table
 * reads `target-edit-user-email` on its own.
 */
export const UserManagementPage: FC = () => {
  const { t } = useTranslation();
  const [maacUserCount, setMaacUserCount] = useState<number | null>(null);

  return (
    <PageLayout>
      <Stack gap="4px">
        <PageTitle>
          {t("userManagement.productAccess.title", "Product access")}
        </PageTitle>
        <Typography variant="body">
          {t(
            "userManagement.productAccess.description",
            "Manage access to each product. MAAC roles are managed here; CAAC and DAAC members are managed in their own products.",
          )}
        </Typography>
      </Stack>
      <ProductAccessCards maacMemberCount={maacUserCount} />
      <Box component="section" id={MAAC_MEMBERS_ANCHOR_ID}>
        <Stack direction="row" alignItems="center" gap="8px" mb="16px">
          <Typography variant="h2" fontWeight={500}>
            {t("userManagement.tabs.maacAccess", "MAAC access")}
          </Typography>
          {maacUserCount === null ? null : (
            <Badge variant="superior">{maacUserCount}</Badge>
          )}
        </Stack>
        <MaacUsers setMaacUserCount={setMaacUserCount} />
      </Box>
    </PageLayout>
  );
};
